import React, { ReactElement } from "react"
import { Toggle, Card, CardHeader, CardText } from "material-ui"
import strings from "../../localization/strings"
import autobind from "class-autobind"
import * as  accountActions from "../../actions/AccountActions"
import { IHierarchyItemForAccountLevel, IHierarchyItem, IJobDocument } from "../../models"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import AccountStore from "../../stores/AccountStore"
import AppStateStore from "../../stores/AppStateStore"
import _ from "lodash"
import HierarchyJobVariations from "./hierarchy_job_variations/HierarchyJobVariations"
import * as permissionHelper from "../../helpers/permissionHelper"

interface IProps {
    cardTitle: string
    useToggleLbl: string
    hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument
    inheritedHierarchyJobItem: IHierarchyItem | IJobDocument
    readonly: boolean
    settingObjPropertyName: string
    handleUpdateJob(job: IJobDocument): Promise<void>
    permissionKey?: string
}
interface IState {
    expand: boolean
    isRoot: boolean
    overridden: boolean
    //settingObj should have Use property
    settingObj: any
}

// we will use this component when allow empty setting.
export default class AllowEmptySettingItem extends React.Component<IProps, IState>  {
    constructor(props) {
        super(props)
        this.state = {
            expand: false,
            isRoot: true,
            overridden: false,
            settingObj: null
        }
        autobind(this)
    }

    componentDidMount() {
        this.checkIsRoot(this.props.hierarchyJobItem)
        this.getSettingValue(this.props.inheritedHierarchyJobItem)
    }

    componentWillReceiveProps(nextProps: IProps) {
        !_.isEqual(this.props.hierarchyJobItem, nextProps.hierarchyJobItem) &&
            this.checkIsRoot(nextProps.hierarchyJobItem)
        !_.isEqual(this.props.inheritedHierarchyJobItem, nextProps.inheritedHierarchyJobItem) &&
            this.getSettingValue(nextProps.inheritedHierarchyJobItem)
    }

    checkIsRoot(hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument) {
        hierarchyJobItem = hierarchyJobItem as IHierarchyItemForAccountLevel
        this.setState({ isRoot: hierarchyJobItem && hierarchyJobItem.level && hierarchyJobItem.level === -1 ? true : false }, () =>
            this.getCanOverride()
        )
    }

    getCanOverride() {
        if (!this.state.isRoot) {
            const hierarchyJobItem = this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)
            const inheritedHierarchyJobItemId = this.props.inheritedHierarchyJobItem ? this.props.inheritedHierarchyJobItem.Id : null
            this.setState({
                overridden: hierarchyJobItem.Id === inheritedHierarchyJobItemId
            })
        }
        else {
            this.setState({ overridden: false })
        }
    }

    getSettingValue(inheritedHierarchyJobItem: IHierarchyItem | IJobDocument) {
        const hierarchySettings = inheritedHierarchyJobItem ?
            inheritedHierarchyJobItem.HierarchyItemSettings :
            AccountStore.getCurrentAccount().HierarchyItemSettings
        const settingObj = hierarchySettings[this.props.settingObjPropertyName]
        this.setState({ settingObj })
    }

    handleSave() {
        this.state.isRoot ?
            this.updateAccountHierarchySettings() :
            this.updateHierarchyJobItemSettings()
    }

    updateAccountHierarchySettings() {
        let hierarchyItemSettings = AccountStore.getCurrentAccount().HierarchyItemSettings
        if (!hierarchyItemSettings) {
            hierarchyItemSettings = {}
        }
        hierarchyItemSettings[this.props.settingObjPropertyName] = this.state.settingObj
        accountActions.patchAccountHierarchySettings(AccountStore.getCurrentAccount().Id, { HierarchyItemSettings: hierarchyItemSettings })
    }

    updateHierarchyJobItemSettings() {
        const { settingObj } = this.state
        const hierarchyJobItem = (this.props.hierarchyJobItem) as (IHierarchyItem | IJobDocument)
        if (this.state.overridden) {
            if (hierarchyJobItem.HierarchyItemSettings) {
                hierarchyJobItem.HierarchyItemSettings[this.props.settingObjPropertyName] = settingObj
            }
            else {
                const hierarchySettings = {}
                hierarchySettings[this.props.settingObjPropertyName] = settingObj
                hierarchyJobItem.HierarchyItemSettings = hierarchySettings
            }
        }
        else {
            if (hierarchyJobItem.HierarchyItemSettings && hierarchyJobItem.HierarchyItemSettings[this.props.settingObjPropertyName]) {
                hierarchyJobItem.HierarchyItemSettings[this.props.settingObjPropertyName].Use = settingObj && settingObj.Use ? true : false
            }
            else {
                hierarchyJobItem.HierarchyItemSettings[this.props.settingObjPropertyName] = null
            }
        }
        hierarchyJobItem.hasOwnProperty("AccountId") ?
            this.props.handleUpdateJob(hierarchyJobItem as IJobDocument).then(() => AppStateStore.setProgressSnackbarContent(null)) :
            accountActions.updateAccountHierarchyItem(AccountStore.getCurrentAccount().Id, hierarchyJobItem, true)
    }

    render() {
        const editable = this.state.isRoot || this.state.overridden
        const useSetting = this.state.settingObj && this.state.settingObj.Use ? true : false
        const parentHasSetting = !this.state.overridden && useSetting
        const canOverride = !this.props.permissionKey || permissionHelper.hasPermission(this.props.permissionKey)

        return (
            <div style={{ position: "relative" }}>
                <Card
                    data-intercom-target={`menu-Hierarchy-Setting-${this.props.settingObjPropertyName}`}
                    expanded={this.state.expand}
                    onExpandChange={expand => this.setState({ expand })}
                >
                    <CardHeader
                        title={this.props.cardTitle}
                        subtitle={[
                            <span key="title">
                                {this.state.isRoot ?
                                    `(${strings.not_inherited})` :
                                    !this.props.inheritedHierarchyJobItem ?
                                        `(${strings.formatString(strings.inherited_from_Account_x, AccountStore.getCurrentAccount().Name)})` :
                                        this.props.inheritedHierarchyJobItem.Id === (this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)).Id ?
                                            `(${strings.not_inherited})` :
                                            `(${strings.formatString(strings.inherited_from_x, `${hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(this.props.inheritedHierarchyJobItem.Id).Label}:  ${(this.props.inheritedHierarchyJobItem as IHierarchyItem).Label}`)})`
                                }
                            </span>,
                            this.state.expand &&
                            <HierarchyJobVariations
                                key="hierarchy job variations"
                                settingPropertyName={this.props.settingObjPropertyName}
                                hierarchyJobItem={this.props.hierarchyJobItem}
                                inheritedHierarchyJobItem={this.props.inheritedHierarchyJobItem}
                            />
                        ]}
                        actAsExpander
                        showExpandableButton
                        titleStyle={{ fontSize: 20, fontWeight: 500, display: "initial" }}
                        subtitleStyle={{ fontSize: 13, color: "rgb(97, 97, 97)", marginLeft: 10, display: "initial" }}
                    />
                    <CardText expandable>
                        {!this.state.isRoot && canOverride &&
                            <Toggle
                                data-intercom-target={`menu-Hierarchy-Settings-${this.props.settingObjPropertyName}-Toggle-Override`}
                                label={strings.Override}
                                style={{ marginBottom: 20, width: 300 }}
                                toggled={this.state.overridden}
                                disabled={this.props.readonly}
                                onToggle={(e, overridden) =>
                                    AppStateStore.setConfirmDialogContent({
                                        content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                        confirm: () => {
                                            const hierarchyJobItem = this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)
                                            let settingObj = hierarchyJobItem && hierarchyJobItem.HierarchyItemSettings && hierarchyJobItem.HierarchyItemSettings[this.props.settingObjPropertyName] ? hierarchyJobItem.HierarchyItemSettings[this.props.settingObjPropertyName] : this.state.settingObj
                                            if (!settingObj) {
                                                settingObj = {}
                                            }
                                            settingObj.Use = overridden
                                            this.setState({
                                                overridden,
                                                settingObj
                                            }, () => this.handleSave())
                                        }
                                    })
                                }
                            />
                        }
                        {(this.state.isRoot || (this.state.overridden || parentHasSetting)) &&
                            <Toggle
                                data-intercom-target={`menu-Hierarchy-Settings-${this.props.settingObjPropertyName}-Toggle-Use`}
                                label={this.props.useToggleLbl}
                                style={{ marginBottom: 20, width: 300 }}
                                disabled={this.props.readonly || !editable}
                                onToggle={(e, useSetting) =>
                                    AppStateStore.setConfirmDialogContent({
                                        content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                        confirm: () => {
                                            const settingObj = this.state.settingObj || {}
                                            settingObj.Use = useSetting
                                            this.setState({
                                                settingObj,
                                                overridden: useSetting
                                            }, () => this.handleSave())
                                        }
                                    })
                                }
                                toggled={useSetting}
                            />
                        }
                        {React.cloneElement(this.props.children as ReactElement, {
                            readonly: this.props.readonly,
                            isRoot: this.state.isRoot,
                            overridden: this.state.overridden,
                            settingObj: this.state.settingObj,
                            handleChangeSettingObj: settingObj => this.setState({ settingObj }, () => this.handleSave())
                        })}
                    </CardText>
                </Card>
            </div>
        )
    }
}